/**
 * Shaka Player-i yükləyir — YALNIZ DRM-li tam film başlayanda.
 *
 * ⚠ MƏNBƏ SIRASI: ÖZ ORIGIN-İMİZ (`shaka-player.compiled.js`, `vite.config.ts →
 *   shakaAsset()` ilə dist-ə düşür) → `cdn.jsdelivr.net` ehtiyat. Səbəb aşağıda
 *   `loadShaka` içindədir (2026-08-04 «Akinak» hadisəsi).
 *
 * ⚠ NİYƏ BUNDLE-DA DEYİL. `vite.config.ts` `inlineDynamicImports: true` ilə hər
 *   şeyi TƏK iife fayla yığır (köhnə TV-də dinamik chunk etibarsızdır). Ona görə
 *   `@kinobox/player`-i import etsək, Shaka (~400 KB) ƏSAS bundle-a düşür və hər
 *   boot-da (sadəcə kataloqa baxanda belə) köhnə TV-ni dondurar. Əvəzinə hls.js
 *   pattern-inin (lib/trailer.ts) eynisi: Shaka yalnız istifadəçi QORUNAN filmi
 *   açanda CDN-dən gəlir. DRM-siz imzalı film native `<video>`-da oynayır (Shaka
 *   yüklənmir), yalnız EXT-X-KEY-li (Widevine/PlayReady/FairPlay) manifest bunu tələb edir.
 *
 * ⚠ TIZEN: `packaging/tizen/config.xml`-də `cdn.jsdelivr.net` `<access>` siyahısında
 *   olmalıdır, yoxsa skript səssizcə bloklanır. Yüklənmə uğursuz olsa `loadShaka`
 *   `null` verir və çağıran treylerə degrade edir (qara ekran yox).
 */

/** Quraşdırılmış `@kinobox/player` ilə eyni versiya — DRM davranışı sınaqdan keçib. */
const SHAKA_CDN = 'https://cdn.jsdelivr.net/npm/shaka-player@4.16.42/dist/shaka-player.compiled.js'

/** İşlətdiyimiz Shaka üzvlərinin minimal tip səthi (global CDN nüsxəsi tiplənmir). */
export interface ShakaNetworkingEngine {
  registerRequestFilter(
    filter: (type: number, request: { headers: Record<string, string> }) => void,
  ): void
}

/** Shaka Player-in yalnız işlətdiyimiz üzvləri — tam tip paketi TV bundle-ına salınmır. */
/**
 * Shaka yolu (variant və ya mətn) — player menyusunun işlətdiyi sahələr.
 * ⚠ Shaka-nın öz `shaka.extern.Track` tipi çox genişdir; CDN-dən gələn
 *   kitabxana üçün tip gətirmirik (bax faylın başındakı izah).
 */
export interface ShakaTrack {
  id: number
  active: boolean
  height?: number | null
  width?: number | null
  bandwidth?: number
  language?: string
  label?: string | null
  kind?: string | null
}

export interface ShakaPlayerLike {
  attach(video: HTMLVideoElement): Promise<void>
  configure(config: unknown): void
  load(uri: string, startTime?: number): Promise<void>
  destroy(): Promise<void>
  addEventListener(type: string, listener: (event: Event) => void): void
  getNetworkingEngine(): ShakaNetworkingEngine | null
  /**
   * ⚠ OPSİONAL: kitabxana CDN-dən gəlir, köhnə nüsxədə metod olmaya bilər.
   *   Menyu hər birini ayrıca yoxlayır (yoxdursa bölmə göstərilmir).
   */
  getVariantTracks?(): ShakaTrack[]
  selectVariantTrack?(track: ShakaTrack, clearBuffer?: boolean): void
  getTextTracks?(): ShakaTrack[]
  selectTextTrack?(track: ShakaTrack): void
  /**
   * ⚠ SƏS DİL SƏVİYYƏSİNDƏ SEÇİLİR (547): `selectVariantTrack` ilə seçsək variant
   *   kilidlənər və ABR həmin dilin keyfiyyət pillələri arasında da adaptasiya edə
   *   bilməz. Shaka-nın öz tövsiyəsi də budur.
   */
  getAudioLanguages?(): string[]
  selectAudioLanguage?(language: string): void
  setTextTrackVisibility?(visible: boolean): void
  isTextTrackVisible?(): boolean
}

/** CDN-dən yüklənən Shaka qlobalının minimal tipi. */
export interface ShakaGlobal {
  Player: {
    new (): ShakaPlayerLike
    isBrowserSupported(): boolean
  }
  polyfill: { installAll(): void }
  net: { NetworkingEngine: { RequestType: { LICENSE: number } } }
}

interface ShakaHost {
  shaka?: ShakaGlobal
}

let shakaPromise: Promise<ShakaGlobal | null> | null = null

/**
 * Shaka-nı CDN-dən yükləyir — YALNIZ DRM-li kontent açılanda. Bundle-a
 * qoşulsaydı hər açılışda köhnə TV-də əlavə yüz kilobayt parse olunardı.
 */
export function loadShaka(): Promise<ShakaGlobal | null> {
  const existing = (window as unknown as ShakaHost).shaka
  if (existing) return Promise.resolve(existing)
  if (shakaPromise) return shakaPromise

  shakaPromise = new Promise<ShakaGlobal | null>((resolve) => {
    /**
     * ⚠ İKİ MƏNBƏ, ÖZ SERVERİMİZ BİRİNCİ (2026-08-04 auditi — sahib bildirişi
     *   «Akinak filmi samsung modelində DRM və ya server xətası verir, ama digər
     *   filmlər açır»).
     *
     *   NİYƏ MƏHZ BU ŞƏKİL: DRM-li film YEGANƏ haldır ki, xarici kitabxana tələb
     *   edir — DRM-siz film native `<video>`-da oynayır və heç nə yükləmir. Yəni
     *   `cdn.jsdelivr.net` həmin TV-nin şəbəkəsindən açılmırsa (operator DNS-i,
     *   Tizen `<access>` siyahısı, filtr) nəticə tam olaraq belə görünür: BİR film
     *   açılmır, qalanları açılır.
     *
     *   İndi fayl `vite.config.ts → shakaAsset()` ilə bizim dist-imizə düşür və
     *   NİSBİ ünvanla istənir — paketlənmiş app-da `file://`, veb TV-də isə
     *   `tv.kinobox.az` üzərindən gəlir. CDN yalnız EHTİYATDIR (fayl deploy
     *   olunmayıbsa köhnə davranış qalır).
     */
    const sources = ['shaka-player.compiled.js', SHAKA_CDN]
    let index = 0

    const done = (): void => {
      const shaka = (window as unknown as ShakaHost).shaka || null
      if (shaka) {
        // Köhnə TV mühərriklərində EME/MSE boşluqlarını doldurur.
        try {
          shaka.polyfill.installAll()
        } catch {
          // polyfill best-effort — uğursuz olsa da player işləyə bilər.
        }
      }
      resolve(shaka)
    }

    const attempt = (): void => {
      const src = sources[index]
      if (!src) {
        shakaPromise = null // növbəti cəhd yenidən yükləməyə çalışsın
        resolve(null)
        return
      }
      index += 1
      const script = document.createElement('script')
      script.src = src
      script.onload = done
      script.onerror = () => {
        // Uğursuz `<script>` DOM-da qalmasın — növbəti cəhd təmiz başlasın.
        if (script.parentNode) script.parentNode.removeChild(script)
        attempt()
      }
      document.head.appendChild(script)
    }

    attempt()
  })
  return shakaPromise
}

/**
 * Dostcasına DRM adı → Shaka/EME açar-sistem identifikatoru.
 *
 * ⚠ Backend lisenziya serverlərini DOSTCASINA adlarla verir (`widevine` və s.),
 *   Shaka isə `drm.servers`-də EME açar-sistem ID gözləyir (`com.widevine.alpha`).
 *   Xəritələmədən Widevine üçün lisenziya serveri TAPILMIR → şifrəli seqment
 *   oynamır. Bu, saytda düzəldilən eyni səhvin TV qarşılığıdır.
 */
const SHAKA_KEY_SYSTEM: Record<string, string> = {
  widevine: 'com.widevine.alpha',
  playready: 'com.microsoft.playready',
  fairplay: 'com.apple.fps',
}

/** Bilet cavabındakı lisenziya ünvanlarını Shaka-nın gözlədiyi formata çevirir. */
export function toShakaServers(
  servers: Record<string, string> | null | undefined,
): Record<string, string> {
  const out: Record<string, string> = {}
  if (!servers) return out
  // ⚠ `Object.entries` ES2017-dir (webOS 3.0-da yoxdur) — `for…in` işlədilir.
  for (const name in servers) {
    if (!Object.prototype.hasOwnProperty.call(servers, name)) continue
    const keySystem = SHAKA_KEY_SYSTEM[name]
    const url = servers[name]
    if (keySystem && url) out[keySystem] = url
  }
  return out
}

/** Manifest DRM-lidirmi — bilet lisenziya serveri verirsə, Shaka lazımdır. */
export function hasDrm(servers: Record<string, string> | null | undefined): boolean {
  if (!servers) return false
  for (const name in servers) {
    if (Object.prototype.hasOwnProperty.call(servers, name) && servers[name]) return true
  }
  return false
}
